import * as React from 'react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { handleButtonAction } from '@/lib/instrumentation/button-instrumentation';

export interface InstrumentedButtonProps extends ButtonProps {
  eventName: string;
  component?: string;
  successMessage?: string;
  errorMessage?: string;
}

export const InstrumentedButton = React.forwardRef<HTMLButtonElement, InstrumentedButtonProps>(
  (
    {
      eventName,
      component = 'unknown',
      successMessage,
      errorMessage = 'Action failed. Please try again.',
      onClick,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const [isLoading, setIsLoading] = React.useState(false);

    const handleClick = React.useCallback(
      async (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!onClick || disabled || isLoading) {
          return;
        }

        setIsLoading(true);
        try {
          const actionOptions: {
            eventName: string;
            component: string;
            successMessage?: string;
            errorMessage?: string;
          } = {
            eventName,
            component,
            errorMessage
          };

          if (successMessage !== undefined) {
            actionOptions.successMessage = successMessage;
          }

          await handleButtonAction(
            () => onClick?.(event),
            actionOptions
          );
        } finally {
          setIsLoading(false);
        }
      },
      [onClick, disabled, isLoading, eventName, component, successMessage, errorMessage]
    );

    return (
      <Button
        ref={ref}
        onClick={handleClick}
        disabled={disabled || isLoading}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

InstrumentedButton.displayName = 'InstrumentedButton';

